import { WIZARD_HASHTAGS_ENABLED } from "../constants/campaignWizard";
import { normalizePost } from "./normalizePost";

const PLATFORM_LABELS = {
    instagram: "Instagram",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    twitter: "X / Twitter",
};

const PLATFORM_ICONS = {
    instagram: { icon: "bi-instagram", iconClass: "text-pink-600" },
    facebook: { icon: "bi-facebook", iconClass: "text-blue-600" },
    linkedin: { icon: "bi-linkedin", iconClass: "text-blue-700" },
    twitter: { icon: "bi-twitter-x", iconClass: "text-slate-800" },
};

function resolvePlatformKey(value) {
    if (!value) return null;

    const lower = String(value).toLowerCase();
    if (PLATFORM_ICONS[lower]) return lower;

    const byLabel = Object.entries(PLATFORM_LABELS).find(
        ([, label]) => label.toLowerCase() === lower
    );
    if (byLabel) return byLabel[0];

    if (lower.includes("instagram")) return "instagram";
    if (lower.includes("facebook")) return "facebook";
    if (lower.includes("linkedin")) return "linkedin";
    if (lower.includes("twitter") || lower === "x") return "twitter";

    return null;
}

export function getPlatformLabel(value) {
    const key = resolvePlatformKey(value);
    return key ? PLATFORM_LABELS[key] : value;
}

export function getPlatformIconMeta(value) {
    const key = resolvePlatformKey(value);
    return key
        ? PLATFORM_ICONS[key]
        : { icon: "bi-share", iconClass: "text-slate-500" };
}

const LANGUAGE_LABELS = {
    english: "English",
    hindi: "Hindi",
    bengali: "Bengali",
};

export function getLanguageLabel(value) {
    return LANGUAGE_LABELS[value] || value;
}

export function formatHashtag(tag) {
    if (tag == null || tag === "") return "";

    if (typeof tag === "object") {
        return formatHashtag(tag.hashtag ?? tag.tag ?? tag.name ?? "");
    }

    const str = String(tag).trim();
    if (!str) return "";

    return str.startsWith("#") ? str : `${str}`;
}

export function normalizeHashtagList(hashtags) {
    if (!Array.isArray(hashtags)) return [];
    return hashtags.map(formatHashtag).filter(Boolean);
}

/** Ensures keyword_focus is always a string array (API may return a space-separated string). */
export function normalizeKeywordFocus(value) {
    if (Array.isArray(value)) {
        return value.map((item) => String(item ?? "").trim()).filter(Boolean);
    }
    if (typeof value === "string") {
        return value.split(/\s+/).map((item) => item.trim()).filter(Boolean);
    }
    return [];
}

function normalizeHashtags(hashtags = []) {
    return normalizeHashtagList(hashtags);
}

export function toOpportunityForApi(opportunity) {
    if (!opportunity?.id) return null;

    return {
        id: opportunity.id,
        title: opportunity.title ?? "",
        opportunity: opportunity.opportunity ?? "",
        description: opportunity.description ?? "",
        industry: opportunity.industry ?? "",
        category: opportunity.category ?? "",
        eventName: opportunity.eventName ?? opportunity.event_name ?? "",
        relevanceScore: opportunity.relevanceScore ?? opportunity.relevance_score ?? null,
        hashtags: normalizeHashtags(opportunity.hashtags || []),
        engagementAngles: opportunity.engagementAngles ?? opportunity.engagement_angles ?? [],
        contentAngles: opportunity.contentAngles ?? opportunity.content_angles ?? [],
    };
}

function buildFollowUpPayload(followUp) {
    const payload = {
        tone: followUp.tone,
        topic: followUp.topic,
        target: followUp.target,
        hashtags: WIZARD_HASHTAGS_ENABLED
            ? normalizeHashtags(followUp.hashtags || [])
            : [],
    };

    const opportunities = (followUp.opportunities ?? [])
        .map(toOpportunityForApi)
        .filter(Boolean);

    if (opportunities.length === 0 && followUp.opportunity) {
        const legacy = toOpportunityForApi(followUp.opportunity);
        if (legacy) opportunities.push(legacy);
    }

    if (opportunities.length > 0) {
        payload.opportunities = opportunities;
    }

    return payload;
}

/** Ideas API — send opportunity ids only; backend loads full docs. */
function buildFollowUpPayloadForIdeas(followUp) {
    const payload = {
        tone: followUp.tone ?? "",
        topic: followUp.topic ?? "",
        target: followUp.target ?? "",
        hashtags: WIZARD_HASHTAGS_ENABLED
            ? normalizeHashtags(followUp.hashtags || [])
            : [],
    };

    const ids = new Set();
    for (const item of followUp.opportunities ?? []) {
        if (item?.id) ids.add(item.id);
    }
    if (followUp.opportunity?.id) ids.add(followUp.opportunity.id);

    if (ids.size > 0) {
        payload.opportunities = [...ids].map((id) => ({ id }));
    }

    return payload;
}

function normalizeLanguageKey(value) {
    const lower = String(value ?? "").trim().toLowerCase();
    if (["english", "hindi", "bengali"].includes(lower)) return lower;
    const match = Object.entries(LANGUAGE_LABELS).find(
        ([, label]) => label.toLowerCase() === lower
    );
    return match?.[0] ?? lower;
}

function normalizeBasicDetailsFromApi(campaign = {}) {
    const source = campaign.basicDetails ?? campaign;
    const platformKey = resolvePlatformKey(source.platform) ?? source.platform ?? "";

    return {
        name: source.name ?? campaign.name ?? "",
        platform: platformKey,
        language: normalizeLanguageKey(source.language ?? campaign.language),
    };
}

function normalizeFollowUpFromApi(campaign = {}) {
    const source = campaign.followUp ?? {};
    const opportunities =
        source.opportunities ??
        (source.opportunity_ids ?? []).map((id) => ({ id })) ??
        [];

    return {
        tone: source.tone ?? "",
        topic: source.topic ?? "",
        target: source.target ?? "",
        hashtags: source.hashtags ?? [],
        opportunities: Array.isArray(opportunities) ? opportunities : [],
    };
}

export function determineWizardStepFromCampaign(campaign = {}) {
    if (campaign.status === "published") return "published";

    const postsCount = campaign.posts?.length ?? campaign.posts_count ?? 0;
    const ideasCount = campaign.ideas?.length ?? campaign.ideas_count ?? 0;

    if (postsCount > 0) return 3;
    if (ideasCount > 0) return 2;
    if (campaign.status === "draft") return 1;
    return 0;
}

export function normalizeCampaignFromApi(data = {}) {
    const basicDetails = normalizeBasicDetailsFromApi(data);
    const followUp = normalizeFollowUpFromApi(data);
    const ideas = Array.isArray(data.ideas) ? data.ideas : [];
    const selectedIdeas = Array.isArray(data.selected_ideas)
        ? data.selected_ideas
        : ideas.filter((idea) => idea.selected);
    const posts = normalizePostsResponse(data);

    return {
        campaignId: data.id ?? data.campaign_id ?? null,
        status: data.status ?? "draft",
        basicDetails,
        followUp,
        ideas,
        selectedIdeas,
        posts,
        wizardStep: determineWizardStepFromCampaign({ ...data, ideas, posts }),
    };
}

export function toApiPayload({ basicDetails, followUp }) {
    return {
        basicDetails: {
            name: basicDetails.name,
            platform: getPlatformLabel(basicDetails.platform),
            language: getLanguageLabel(basicDetails.language),
        },
        followUp: buildFollowUpPayload(followUp),
    };
}

export function toIdeasApiPayload({ basicDetails, followUp }) {
    return {
        basicDetails: {
            name: basicDetails.name,
            platform: getPlatformLabel(basicDetails.platform),
            language: getLanguageLabel(basicDetails.language),
        },
        followUp: buildFollowUpPayloadForIdeas(followUp),
    };
}

export function toGeneratePostsCombinedPayload({ campaignId, basicDetails, followUp }) {
    return {
        ...toIdeasApiPayload({ basicDetails, followUp }),
        campaignId: campaignId ?? null,
    };
}

export function toGeneratePostsGuestPayload({ campaignId, basicDetails, followUp, guestId }) {
    return {
        ...toGeneratePostsCombinedPayload({ campaignId, basicDetails, followUp }),
        guestId: guestId ?? null,
    };
}

function mapIdeaForApi({
    id,
    idea_title,
    angle,
    relevance_to_trends,
    keyword_focus,
    asset_type,
}) {
    return {
        id,
        idea_title,
        angle,
        relevance_to_trends,
        keyword_focus: normalizeKeywordFocus(keyword_focus),
        asset_type: asset_type ?? null,
    };
}

export function toSelectedIdeasForApi(ideas) {
    return ideas.map(mapIdeaForApi);
}

export function toGeneratePostPayload({ campaignId, basicDetails, followUp, selectedIdeas }) {
    return {
        campaignId,
        ...toApiPayload({ basicDetails, followUp }),
        selected_ideas: toSelectedIdeasForApi(selectedIdeas),
    };
}

export function normalizePostsResponse(data) {
    if (Array.isArray(data)) return data.map(normalizePost);
    if (Array.isArray(data?.posts)) return data.posts.map(normalizePost);
    return [];
}

export function normalizeGeneratePostResponse(data = {}) {
    return {
        campaignId: data.campaign_id ?? data.campaign?.id ?? data.campaignId ?? null,
        guestId: data.guest_id ?? data.guestId ?? null,
        campaign: data.campaign ?? null,
        status: data.status ?? data.campaign?.status ?? "draft",
        posts: normalizePostsResponse(data),
    };
}

export function toCreateCampaignPayload({ basicDetails, followUp, selectedIdeas, posts }) {
    return {
        ...toApiPayload({ basicDetails, followUp }),
        selected_ideas: selectedIdeas.map(mapIdeaForApi),
        posts: posts.map(({ id, caption, hash_tags, media, asset_type }) => ({
            id,
            caption,
            hash_tags,
            media,
            asset_type: asset_type ?? null,
        })),
    };
}

export function toPublishCampaignPayload({ basicDetails, followUp, selectedIdeas, posts }) {
    return toCreateCampaignPayload({ basicDetails, followUp, selectedIdeas, posts });
}

export function normalizeCreateCampaignResponse(data) {
    if (data?.campaign) return data.campaign;
    if (data?.id) return data;
    return data;
}

export function normalizePublishCampaignResponse(data) {
    return normalizeCreateCampaignResponse(data);
}
